import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { CarouselIcon } from '~/components/CommonCarousel';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import { usePrevNextButtons } from '~/hooks/usePrevNextButtons';

import { userStore } from '~/stores';

import CategoryBlockTitle from '../CategoryBlockTitle';
import { cfSrcForImage, createCategoryUrl } from '../utils';

import styles from './CategoriesGridOneSBlock.module.scss';

export interface CategoriesGridOneSItem {
  img: string | null;
  title: string;
  id: number | string;
}

interface MobileDisplayProps {
  items: CategoriesGridOneSItem[];
  tileMaskIncluded?: boolean;
}

const SLIDE_SIZE = 285;

const MobileDisplay = ({ items, tileMaskIncluded }: MobileDisplayProps) => {
  const { handleClickCategory } = useCategoryAnalytics();

  return (
    <div className={styles.grid}>
      {items.slice(0, 4).map((item) => {
        const slideImage = `url('${cfSrcForImage(item.img || '', {
          width: SLIDE_SIZE,
          height: SLIDE_SIZE,
        })}')`;
        const backgroundImage = tileMaskIncluded
          ? `var(--tileMaskHalf), ${slideImage}`
          : slideImage;

        return (
          <div
            className={styles.item}
            key={item.title}
            style={{ backgroundImage }}
          >
            <Link
              onClick={handleClickCategory(item.id)}
              to={createCategoryUrl({ id: item.id })}
              className={styles.link}
            >
              <div className={styles.title}>{item.title}</div>
            </Link>
          </div>
        );
      })}
    </div>
  );
};

interface DesktopDisplayProps {
  items: CategoriesGridOneSItem[];
  tileMaskIncluded?: boolean;
}

const DesktopDisplay = ({ items, tileMaskIncluded }: DesktopDisplayProps) => {
  const { handleClickCategory } = useCategoryAnalytics();
  const { isMobile } = useGlobal();
  const [api, setApi] = useState<CarouselApi | undefined>(undefined);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(api);

  return (
    <div
      className={classes([styles.desktopContainer, 'banner-carousel carousel'])}
    >
      {!isMobile && !prevBtnDisabled && (
        <CarouselIcon
          position="left"
          variant="fill"
          onClick={onPrevButtonClick}
        />
      )}
      <Carousel
        setApi={setApi}
        opts={{
          align: 'start' as const,
          slidesToScroll: 1,
          containScroll: 'trimSnaps' as const,
        }}
        className={styles.grid}
        dir={userStore.dir}
        key={userStore.dir}
      >
        <CarouselContent>
          {items.map((item) => {
            const slideImage = `url('${cfSrcForImage(item.img || '', {
              width: SLIDE_SIZE,
              height: SLIDE_SIZE,
            })}')`;
            const backgroundImage = tileMaskIncluded
              ? `var(--tileMask), ${slideImage}`
              : slideImage;

            return (
              <CarouselItem
                className={styles.item}
                key={item.title}
                style={{ backgroundImage }}
              >
                <Link
                  data-link={`category-${item.id}`}
                  onClick={handleClickCategory(item.id)}
                  to={createCategoryUrl({ id: item.id })}
                  className={styles.link}
                >
                  <div className={styles.title}>{item.title}</div>
                </Link>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
      {!isMobile && !nextBtnDisabled && (
        <CarouselIcon
          position="right"
          variant="fill"
          onClick={onNextButtonClick}
        />
      )}
    </div>
  );
};

interface CategoriesGridOneSBlockProps {
  title: string;
  description: Nullable<string>;
  backgroundColor: Nullable<string>;
  items: CategoriesGridOneSItem[];
  link: string;
  isMobile: boolean;
  tileMaskIncluded?: boolean;
}

const CategoriesGridOneSBlock = ({
  title,
  description,
  backgroundColor,
  items,
  link,
  isMobile,
  tileMaskIncluded,
}: CategoriesGridOneSBlockProps) => {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <CategoryBlockTitle
        text={title}
        linkTo={link}
        description={description}
        backgroundColor={backgroundColor}
      />
      {isMobile ? (
        <MobileDisplay items={items} tileMaskIncluded={tileMaskIncluded} />
      ) : (
        <DesktopDisplay items={items} tileMaskIncluded={tileMaskIncluded} />
      )}
    </div>
  );
};

export default observer(CategoriesGridOneSBlock);
